import moment from "moment";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import classNames from "classnames";

// react-calendar is 280px wide + the popup's 1px borders.
const POPUP_WIDTH = 282;

// Single-input MM/DD/YYYY date entry. Replaces the prior three-segment
// (month / day / year-autocomplete) layout that was eating horizontal space
// in the order form. Auto-formats digits as the user types, validates on
// blur, and emits the same `onChange("MM/DD/YYYY")` string contract every
// consumer (order form, ModalEditOrder, CMS-1500 mapping, etc.) already
// depends on. Calendar popup + Today button + onType + disabled all
// preserved.
const DatePicker = ({
  value,
  onChange,
  onKeyDown,
  onType,
  disabled,
  className,
  showTodayButton = false,
  // eslint-disable-next-line no-unused-vars
  futureFirst = false, // accepted for API back-compat (year dropdown removed)
  showCalendar = true,
  calendarMinDetail = "year",
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [error, setError] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const calendarRef = useRef(null); // trigger (icon button container) — anchor + click-outside
  const popupRef = useRef(null); // portaled calendar popup
  const inputRef = useRef(null);

  const MIN_YEAR = 1900;

  // Insert "/" after MM and DD as the user types. Strip non-digits first so
  // pasting "2026-05-19" lands cleanly. Cap at 8 digits → 10 chars with
  // slashes; the input's maxLength is the belt-and-suspenders second cap.
  const formatInput = (raw) => {
    const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  // Sync the input from the external `value` prop. Accepts "MM/DD/YYYY" or
  // "YYYY-MM-DD" (matches the prior implementation's parse list so existing
  // persisted orders show their saved date unchanged on re-open).
  useEffect(() => {
    if (!value) {
      setText("");
      setError(false);
      return;
    }
    for (const fmt of ["MM/DD/YYYY", "YYYY-MM-DD"]) {
      if (moment(value, fmt, true).isValid()) {
        setText(moment(value, fmt).format("MM/DD/YYYY"));
        setError(false);
        return;
      }
    }
  }, [value]);

  // Click-outside handler for calendar popup. The popup is portaled to
  // <body>, so it is NOT inside calendarRef — check both the trigger and the
  // popup, otherwise clicking a day would register as "outside" and close it.
  useEffect(() => {
    if (!calendarOpen) return;
    const handleClickOutside = (e) => {
      const insideTrigger = calendarRef.current?.contains(e.target);
      const insidePopup = popupRef.current?.contains(e.target);
      if (!insideTrigger && !insidePopup) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [calendarOpen]);

  // Position the portaled popup against the trigger using viewport coords
  // (position: fixed). Right-aligned to the trigger, flipped above / clamped
  // into the viewport when it would overflow. Recomputed on scroll (capture,
  // to catch the modal's inner scroll container) and resize. Portaling to
  // <body> escapes the modal's `overflow` clip and stacking context, which is
  // what was cutting the calendar off on the left and letting form banners
  // paint over it.
  useLayoutEffect(() => {
    if (!calendarOpen) return;
    const updatePosition = () => {
      const trigger = calendarRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const margin = 8;
      let left = rect.right - POPUP_WIDTH;
      left = Math.max(margin, Math.min(left, window.innerWidth - POPUP_WIDTH - margin));
      const popupHeight = popupRef.current?.offsetHeight || 340;
      let top = rect.bottom + 4;
      if (top + popupHeight > window.innerHeight - margin) {
        const above = rect.top - popupHeight - 4;
        top = above >= margin ? above : Math.max(margin, window.innerHeight - popupHeight - margin);
      }
      setPopupPos({ top, left });
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [calendarOpen]);

  const isValidDate = (str) => {
    if (!str || str.length !== 10) return false;
    const m = moment(str, "MM/DD/YYYY", true);
    return m.isValid() && m.year() >= MIN_YEAR;
  };

  const emitIfValid = (str) => {
    if (isValidDate(str)) {
      onChange(moment(str, "MM/DD/YYYY").format("MM/DD/YYYY"));
    }
  };

  const handleChange = (e) => {
    const formatted = formatInput(e.target.value);
    setText(formatted);
    if (onType) onType(formatted);

    // Live-emit when fully typed; clear error if user is mid-edit and not
    // yet at full length so the field doesn't flash red on every keystroke.
    if (formatted.length === 10) {
      if (isValidDate(formatted)) {
        emitIfValid(formatted);
        setError(false);
      } else {
        setError(true);
      }
    } else {
      setError(false);
    }
  };

  const handleBlur = () => {
    if (!text) {
      setError(false);
      return;
    }
    setError(!isValidDate(text));
  };

  const handleKeyDownInternal = (e) => {
    if (e.key === "Enter" && onKeyDown) {
      onKeyDown(e);
    }
  };

  const handleCalendarSelect = (date) => {
    const formatted = moment(date).format("MM/DD/YYYY");
    setText(formatted);
    setError(false);
    setCalendarOpen(false);
    onChange(formatted);
  };

  const parsedDateValue = useMemo(() => {
    if (!isValidDate(text)) return null;
    const m = moment(text, "MM/DD/YYYY", true);
    return m.isValid() ? m.toDate() : null;
  }, [text]);

  return (
    <div className={classNames("datepicker-wrapper", className)}>
      <div className="datepicker-fields">
        <input
          type="text"
          ref={inputRef}
          className={classNames("date-single", { error })}
          placeholder="MM/DD/YYYY"
          value={text}
          inputMode="numeric"
          maxLength={10}
          autoComplete="off"
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDownInternal}
          disabled={disabled}
        />
        {showCalendar && !disabled && (
          <div className="datepicker-calendar-trigger" ref={calendarRef}>
            <button
              type="button"
              className="datepicker-calendar-icon"
              onClick={() => setCalendarOpen(!calendarOpen)}
              aria-label={t("common.openCalendar")}
            >
              <i className="material-symbols-outlined">calendar_today</i>
            </button>
            {calendarOpen &&
              createPortal(
                <div className="datepicker-calendar-popup" ref={popupRef} style={{ top: popupPos.top, left: popupPos.left }}>
                  <Calendar onChange={handleCalendarSelect} value={parsedDateValue} minDetail={calendarMinDetail} />
                </div>,
                document.body
              )}
          </div>
        )}
      </div>
      {error && <span className="date-field-error">{t("common.mustBeValidYear")}</span>}
      {!disabled && showTodayButton && (
        <div className="datepicker-actions">
          <button
            type="button"
            className="small blue-border ml-10"
            onClick={() => {
              const today = moment().format("MM/DD/YYYY");
              setText(today);
              setError(false);
              onChange(today);
            }}
          >
            {t("common.today")}
          </button>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
